import { useContext, useState } from 'react';
import { Bell, User, LogOut, Menu } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import Sidebar from './components/Sidebar';
import { useToast } from '../../context/ToastContext';

export default function Dashboard() {
  const { logoutUser,  hasAnyRole,isAuthenticated,user } = useContext(AuthContext);
  const { showError, showSuccess } = useToast(); // Use the toast hook
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // No automatic navigation needed here - routing is handled by React Router
  // The PrivateRoute component already handles authentication checks

  const getRoleDisplayName = (role) => {
    return role || 'User';
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/dashboard/risks')) {
      if (path.includes('/add')) return 'Ajouter un risque';
      if (path.includes('/edit')) return 'Modifier le risque';
      return 'Gérer les risques';
    }
    if (path.startsWith('/dashboard/cartographies')) {
      if (path.includes('/add')) return 'Ajouter une cartographie';
      if (path.includes('/edit')) return 'Modifier la cartographie';
      if (path.includes('/view')) return 'Voir les cartographies';
      return 'Gérer les cartographies';
    }
    if (path.startsWith('/dashboard/activities')) {
      if (path.includes('/add')) return 'Ajouter une activité';
      if (path.includes('/edit')) return 'Modifier l\'activité';
      return 'Gérer les activités';
    }
    if (path.startsWith('/dashboard/admin/users')) {
      if (path.includes('/add')) return 'Ajouter un utilisateur';
      return 'Gérer les utilisateurs';
    }
    if (path === '/dashboard/user-roles') return 'Gérer les rôles';
    if (path === '/dashboard/action-logs') return 'Journaux d\'actions';
    if (path.startsWith('/dashboard/admin/logs')) {
      if (path === '/dashboard/admin/logs') return 'Journaux d\'actions';
      if (path === '/dashboard/admin/logs/all') return 'Tous les journaux';
      if (path === '/dashboard/admin/logs/risks') return 'Journaux des risques';
      if (path === '/dashboard/admin/logs/processes') return 'Journaux des processus';
      if (path === '/dashboard/admin/logs/users') return 'Journaux des utilisateurs';
      if (path === '/dashboard/admin/logs/sessions') return 'Journaux des sessions';
      return 'Journaux d\'actions';
    }
    
    return 'Dashboard';
  };

return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Click outside to close user dropdown */}
      {isUserDropdownOpen && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setIsUserDropdownOpen(false)}
        />
      )}

      {/* Sidebar Component */}
      <Sidebar 
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
      />

     
      {hasAnyRole(["Admin","RiskManager","RiskIdentifier","CartoViewer", "AuditManager", "AuditUser"]) && (
      
      <div className="lg:ml-64">
        {/* Top Header - Fixed */}
        <header className="fixed top-0 right-0 left-0 lg:left-64 z-20 bg-white border-b border-gray-200 h-14 flex items-center justify-between px-6 shadow-sm">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* <button className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full"></span>
            </button> */}
            
            <div className="relative">
              
              <div className='flex items-center space-x-2'>
                  <p className='text-sm font-medium text-gray-700'>{user.firstName} {user.lastName}</p>
                  <button
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="w-8 h-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center hover:shadow-md transition-all duration-200"
                  >
                    <User className="h-4 w-4 text-green-500" /> 
                  </button>
              </div>
              

              {/* User Dropdown */}
              {isUserDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 bg-gradient-to-r from-green-600 to-green-700 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{getRoleDisplayName(user?.role?.name)}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area - This will render the nested routes */}
        <main className="min-h-screen bg-gray-50 pt-14">
          <div className="max-w-7xl mx-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
      )}
    </div>
  );
}